import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectAppReconnecting } from 'features/application/applicationSlice';
import { useOnReconnected } from 'hooks/useOnReconnected';
import { SidePanelType } from 'features/side-panels/types';

const POLL_MAX = 5;
const pollDelay = (attempt: number) => Math.min(2000 * 2 ** attempt, 30_000);

type UsePanelRecoveryParams = {
  visible: Set<SidePanelType>;
  reload: (panelName: SidePanelType) => void;
};

export const usePanelRecovery = ({ visible, reload }: UsePanelRecoveryParams) => {
  const [attempts, setAttempts] = useState<Partial<Record<SidePanelType, number>>>({});

  const failedRef = useRef<Set<SidePanelType>>(new Set());
  const timersRef = useRef<Partial<Record<SidePanelType, ReturnType<typeof setTimeout>>>>({});

  const isReconnecting = useAppSelector(selectAppReconnecting);

  const clearRetry = useCallback((panelName: SidePanelType) => {
    const timer = timersRef.current[panelName];
    if (timer) {
      clearTimeout(timer);
      delete timersRef.current[panelName];
    }
  }, []);

  const reset = useCallback(
    (panelName: SidePanelType) => {
      clearRetry(panelName);
      failedRef.current.delete(panelName);
      setAttempts((prev) => {
        const { [panelName]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [clearRetry]
  );

  const retry = useCallback(
    (panelName: SidePanelType) => {
      setAttempts((prev) => ({ ...prev, [panelName]: (prev[panelName] ?? 0) + 1 }));
      reload(panelName);
    },
    [reload]
  );

  useEffect(() => {
    failedRef.current.forEach((panelName) => {
      const attempt = attempts[panelName] ?? 0;
      const canPoll = visible.has(panelName) && !isReconnecting && attempt < POLL_MAX;
      if (!canPoll) {
        clearRetry(panelName);
        return;
      }
      if (timersRef.current[panelName]) {
        return;
      }
      timersRef.current[panelName] = setTimeout(() => {
        delete timersRef.current[panelName];
        retry(panelName);
      }, pollDelay(attempt));
    });
  }, [isReconnecting, attempts, visible, retry, clearRetry]);

  useEffect(() => () => Object.values(timersRef.current).forEach((t) => t && clearTimeout(t)), []);

  useOnReconnected(() => {
    failedRef.current.forEach((panelName) => {
      clearRetry(panelName);
      if (visible.has(panelName)) {
        retry(panelName);
      }
    });
  });

  const onError = useCallback((panelName: SidePanelType) => {
    failedRef.current.add(panelName);
    setAttempts((prev) => (panelName in prev ? prev : { ...prev, [panelName]: 0 }));
  }, []);

  const onLoaded = useCallback(
    (panelName: SidePanelType) => {
      if (!failedRef.current.has(panelName)) {
        return;
      }
      reset(panelName);
    },
    [reset]
  );

  const retryNow = useCallback(
    (panelName: SidePanelType) => {
      if (!failedRef.current.has(panelName)) {
        return;
      }
      reset(panelName);
      reload(panelName);
    },
    [reset, reload]
  );

  const isExhausted = useCallback(
    (panelName: SidePanelType) => (attempts[panelName] ?? 0) >= POLL_MAX,
    [attempts]
  );

  return { onError, onLoaded, retryNow, isExhausted };
};
